
import React, { useEffect, useMemo, useState } from 'react';
import './Dashboard.css';
import './SalesScoreboard.css';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import DatePicker from './DatePicker';
import { getDocFee, docFeeIncome } from '../utils/docFee';
import { overUnderFor, avgOverUnder, tradeCount } from '../utils/overUnder';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function abbreviateNumber(value) {
  if (value === undefined || value === null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'K';
  return value.toLocaleString();
}

function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0';
  return '$' + abbreviateNumber(Math.round(value));
}

function formatNumber(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '0';
  return abbreviateNumber(Number(value));
}

function calculatePercent(actual, target) {
  if (!target || target === 0) return 0;
  return Math.round((actual / target) * 100);
}

function ScoreboardDropdown({ options, value, onChange }) {
  return (
    <select className="scoreboard-dropdown" value={value} onChange={e => onChange(e.target.value)}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// Compute fiscal meta from dealership settings
function getFiscalMetaForDealership(dealership) {
  const mode = dealership?.performanceYearMode || 'CALENDAR';
  const startMonth = Math.min(Math.max(Number(dealership?.fiscalYearStartMonth) || 1, 1), 12);
  const startDay = Math.min(Math.max(Number(dealership?.fiscalYearStartDay) || 1, 1), 28);
  const today = new Date();
  let fiscalStart = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);
  if (mode === 'FISCAL' && today < fiscalStart) {
    fiscalStart.setFullYear(fiscalStart.getFullYear() - 1);
  }
  const fiscalEnd = new Date(fiscalStart);
  fiscalEnd.setFullYear(fiscalStart.getFullYear() + 1);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysInYear = Math.round((fiscalEnd.getTime() - fiscalStart.getTime()) / msPerDay);
  const daysElapsed = Math.max(0, Math.floor((today.getTime() - fiscalStart.getTime()) / msPerDay));
  const weeksInYear = Math.ceil(daysInYear / 7);
  const monthsInYear = 12;
  return { fiscalStart, fiscalEnd, daysInYear, daysElapsed, weeksInYear, monthsInYear };
}

// Get monthly target value from monthlyTargets array for current month
function getMonthlyTarget(targets, field, currentMonth) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }

  // currentMonth is 0-based (0 = January), monthlyTargets uses 1-based (1 = January)
  const monthData = targets.monthlyTargets.find(m => m.month === currentMonth + 1);
  if (!monthData) return 0;

  return Number(monthData[field]) || 0;
}

// MTD target = the SELECTED month's target prorated to the days elapsed through the
// reference date, matching the single-month, day-capped MTD actuals this scoreboard
// compares against (and consistent with the Sales Leader / Visual scoreboards). Uses
// the reference date's month/day so looking back at a past month shows that month's
// target instead of the current calendar month.
function getMTDTarget(targets, field, referenceDate = new Date()) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }
  const ref = new Date(referenceDate);
  const refMonth = ref.getMonth();
  const monthlyTarget = getMonthlyTarget(targets, field, refMonth);
  const daysInMonth = new Date(ref.getFullYear(), refMonth + 1, 0).getDate();
  const daysElapsed = Math.min(ref.getDate(), daysInMonth);
  return Math.round(monthlyTarget * (daysElapsed / daysInMonth));
}

// Convert stored target to requested period using fiscal-aware counts
// This is used for activity metrics (greets, sit-downs, etc.) that still use frequency selectors
function convertTargetValue(targets, field, period, fiscalMeta) {
  if (!targets) return 0;
  const base = Number(targets[field]) || 0;
  const activityFields = ['greets', 'sitDowns', 'writeUps', 'testRides'];
  const storedFreq = activityFields.includes(field)
    ? (targets.metricsFrequency || 'daily')
    : (targets.frontEndFrequency || 'yearly');

  const fromFreq = (storedFreq || 'yearly').toLowerCase();
  const toFreq = (period || 'yearly').toLowerCase();

  // Convert to yearly total first
  let yearlyTotal;
  switch (fromFreq) {
    case 'daily':
      yearlyTotal = base * fiscalMeta.daysInYear;
      break;
    case 'weekly':
      yearlyTotal = base * fiscalMeta.weeksInYear;
      break;
    case 'monthly':
      yearlyTotal = base * fiscalMeta.monthsInYear;
      break;
    case 'yearly':
    default:
      yearlyTotal = base;
  }

  // Convert yearly total to requested display period
  switch (toFreq) {
    case 'daily':
      return Math.round(yearlyTotal / fiscalMeta.daysInYear);
    case 'weekly':
      return Math.round(yearlyTotal / fiscalMeta.weeksInYear);
    case 'monthly':
      return Math.round(yearlyTotal / fiscalMeta.monthsInYear);
    case 'yearly':
    default:
      return Math.round(yearlyTotal);
  }
}

function computeMetrics(logs, activities, targets, dealership, referenceDate = new Date()) {
  const refDate = new Date(referenceDate);
  const now = refDate;
  const month = now.getMonth();
  const year = now.getFullYear();

  const fiscalMeta = getFiscalMetaForDealership(dealership);

  // Helper to parse UTC date and extract components without timezone conversion
  const getUTCDateComponents = (dateStr) => {
    const d = new Date(dateStr);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth(),
      date: d.getUTCDate()
    };
  };

  // Get reference date components
  const refDateComp = {
    year: refDate.getFullYear(),
    month: refDate.getMonth(),
    date: refDate.getDate()
  };

  // Filter logs for daily and monthly
  const dailyLogs = logs.filter((e) => {
    const logDateComp = getUTCDateComponents(e.date);
    return logDateComp.year === refDateComp.year &&
           logDateComp.month === refDateComp.month &&
           logDateComp.date === refDateComp.date;
  });

  // MTD: all data from start of month through the selected date (inclusive)
  const monthlyLogs = logs.filter((e) => {
    const logDateComp = getUTCDateComponents(e.date);
    return logDateComp.month === month &&
           logDateComp.year === year &&
           (logDateComp.year < year ||
            (logDateComp.year === year && logDateComp.month < month) ||
            (logDateComp.year === year && logDateComp.month === month && logDateComp.date <= refDateComp.date));
  });

  const sumLogs = (list, field) => list.reduce((sum, e) => sum + (Number(e[field]) || 0), 0);

  // Calculate greets, sitDowns, writeUps for daily and monthly
  const greets = {
    daily: sumLogs(dailyLogs, 'greets'),
    monthly: sumLogs(monthlyLogs, 'greets'),
    dailyTarget: convertTargetValue(targets, 'greets', 'daily', fiscalMeta),
    monthlyTarget: convertTargetValue(targets, 'greets', 'monthly', fiscalMeta)
  };

  const sitDowns = {
    daily: sumLogs(dailyLogs, 'sitDowns'),
    monthly: sumLogs(monthlyLogs, 'sitDowns'),
    dailyTarget: convertTargetValue(targets, 'sitDowns', 'daily', fiscalMeta),
    monthlyTarget: convertTargetValue(targets, 'sitDowns', 'monthly', fiscalMeta)
  };

  const writeUps = {
    daily: sumLogs(dailyLogs, 'writeUps'),
    monthly: sumLogs(monthlyLogs, 'writeUps'),
    dailyTarget: convertTargetValue(targets, 'writeUps', 'daily', fiscalMeta),
    monthlyTarget: convertTargetValue(targets, 'writeUps', 'monthly', fiscalMeta)
  };

  const testRides = {
    daily: sumLogs(dailyLogs, 'testRides'),
    monthly: sumLogs(monthlyLogs, 'testRides'),
    dailyTarget: convertTargetValue(targets, 'testRides', 'daily', fiscalMeta),
    monthlyTarget: convertTargetValue(targets, 'testRides', 'monthly', fiscalMeta)
  };

  // Filter activities by period
  const dailyActivities = activities.filter((a) => {
    const saleDateComp = getUTCDateComponents(a.saleDate);
    return saleDateComp.year === refDateComp.year &&
           saleDateComp.month === refDateComp.month &&
           saleDateComp.date === refDateComp.date;
  });

  // MTD: all sales from start of month through the selected date (inclusive)
  const monthlyActivities = activities.filter((a) => {
    const saleDateComp = getUTCDateComponents(a.saleDate);
    return saleDateComp.month === month &&
           saleDateComp.year === year &&
           (saleDateComp.year < year ||
            (saleDateComp.year === year && saleDateComp.month < month) ||
            (saleDateComp.year === year && saleDateComp.month === month && saleDateComp.date <= refDateComp.date));
  });

  // Over/under on a trade is a cost of moving the unit, so it comes out of that
  // unit's gross at report time. The stored totalCost is left alone.
  const buildSalesBlock = (list, filterNew) => {
    const subset = list.filter((a) => {
      const val = (a.motorcycleNewUsed || '').toUpperCase();
      return filterNew ? val === 'NEW' : val === 'USED';
    });
    const count = subset.length;
    const gross = subset.reduce((sum, a) => {
      const price = Number(a.salesPrice) || 0;
      const cost = Number(a.totalCost) || 0;
      return sum + (price - cost - (overUnderFor(a) || 0));
    }, 0);
    return { count, gross };
  };

  const dailyNew = buildSalesBlock(dailyActivities, true);
  const dailyUsed = buildSalesBlock(dailyActivities, false);
  const monthlyNew = buildSalesBlock(monthlyActivities, true);
  const monthlyUsed = buildSalesBlock(monthlyActivities, false);

  // Get targets from monthly targets array
  // MTD = selected month's target prorated to the selected day
  const newUnitsMTD = getMTDTarget(targets, 'newUnits', refDate);
  const usedUnitsMTD = getMTDTarget(targets, 'usedUnits', refDate);
  const newGrossMTD = getMTDTarget(targets, 'newGrossProfit', refDate);
  const usedGrossMTD = getMTDTarget(targets, 'usedGrossProfit', refDate);
  const backEndGrossMTD = getMTDTarget(targets, 'backEndGrossProfit', refDate);
  const fiGrossMTD = getMTDTarget(targets, 'fiGrossProfit', refDate);

  // Calculate daily targets by dividing current month's target by days in month
  const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const newUnitsDaily = Math.round(getMonthlyTarget(targets, 'newUnits', month) / daysInCurrentMonth);
  const usedUnitsDaily = Math.round(getMonthlyTarget(targets, 'usedUnits', month) / daysInCurrentMonth);
  const newGrossDaily = Math.round(getMonthlyTarget(targets, 'newGrossProfit', month) / daysInCurrentMonth);
  const usedGrossDaily = Math.round(getMonthlyTarget(targets, 'usedGrossProfit', month) / daysInCurrentMonth);
  const backEndGrossDaily = Math.round(getMonthlyTarget(targets, 'backEndGrossProfit', month) / daysInCurrentMonth);
  const fiGrossDaily = Math.round(getMonthlyTarget(targets, 'fiGrossProfit', month) / daysInCurrentMonth);

  // Total unit and gross targets
  const totalUnitsMTD = newUnitsMTD + usedUnitsMTD;
  const totalUnitsDaily = newUnitsDaily + usedUnitsDaily;
  const totalGrossMTD = newGrossMTD + usedGrossMTD + backEndGrossMTD;
  const totalGrossDaily = newGrossDaily + usedGrossDaily + backEndGrossDaily;

  const dailyTotalUnits = dailyNew.count + dailyUsed.count;
  const dailyTotalGross = dailyNew.gross + dailyUsed.gross;
  const monthlyTotalUnits = monthlyNew.count + monthlyUsed.count;
  const monthlyTotalGross = monthlyNew.gross + monthlyUsed.gross;

  // F&I calculations. Doc fee is back-end gross too, so it joins the total while
  // the reserve and product breakdowns stay as F&I actually wrote them.
  const docFee = getDocFee(targets);
  const calculateFI = (activityList) => {
    const fiReserve = activityList.reduce((sum, a) => sum + (Number(a.fiReserve) || 0), 0);
    const fiProduct = activityList.reduce((sum, a) => sum + (Number(a.fiProduct) || 0), 0);
    const fiDocFee = docFeeIncome(activityList, docFee);
    return { fiReserve, fiProduct, fiDocFee, fiTotal: fiReserve + fiProduct + fiDocFee };
  };

  const dailyFI = calculateFI(dailyActivities);
  const monthlyFI = calculateFI(monthlyActivities);

  return {
    greets,
    sitDowns,
    writeUps,
    testRides,
    newUnits: {
      daily: dailyNew.count,
      monthly: monthlyNew.count,
      dailyTarget: newUnitsDaily,
      monthlyTarget: newUnitsMTD
    },
    newGross: {
      daily: dailyNew.gross,
      monthly: monthlyNew.gross,
      dailyTarget: newGrossDaily,
      monthlyTarget: newGrossMTD
    },
    usedUnits: {
      daily: dailyUsed.count,
      monthly: monthlyUsed.count,
      dailyTarget: usedUnitsDaily,
      monthlyTarget: usedUnitsMTD
    },
    usedGross: {
      daily: dailyUsed.gross,
      monthly: monthlyUsed.gross,
      dailyTarget: usedGrossDaily,
      monthlyTarget: usedGrossMTD
    },
    frontendTotal: {
      daily: dailyTotalGross,
      monthly: monthlyTotalGross,
      dailyTarget: totalGrossDaily,
      monthlyTarget: totalGrossMTD
    },
    frontendPerUnit: {
      daily: dailyTotalUnits > 0 ? dailyTotalGross / dailyTotalUnits : 0,
      monthly: monthlyTotalUnits > 0 ? monthlyTotalGross / monthlyTotalUnits : 0,
      dailyTarget: totalUnitsDaily > 0 ? totalGrossDaily / totalUnitsDaily : 0,
      monthlyTarget: totalUnitsMTD > 0 ? totalGrossMTD / totalUnitsMTD : 0
    },
    fiReserve: {
      daily: dailyFI.fiReserve,
      monthly: monthlyFI.fiReserve,
      dailyTarget: 0, // F&I Reserve is part of fiGrossProfit now
      monthlyTarget: 0
    },
    fiProduct: {
      daily: dailyFI.fiProduct,
      monthly: monthlyFI.fiProduct,
      dailyTarget: 0, // F&I Product is part of fiGrossProfit now
      monthlyTarget: 0
    },
    fiTotal: {
      daily: dailyFI.fiTotal,
      monthly: monthlyFI.fiTotal,
      dailyTarget: fiGrossDaily,
      monthlyTarget: fiGrossMTD
    },
    fiPerUnit: {
      daily: dailyTotalUnits > 0 ? dailyFI.fiTotal / dailyTotalUnits : 0,
      monthly: monthlyTotalUnits > 0 ? monthlyFI.fiTotal / monthlyTotalUnits : 0,
      dailyTarget: totalUnitsDaily > 0 ? fiGrossDaily / totalUnitsDaily : 0,
      monthlyTarget: totalUnitsMTD > 0 ? fiGrossMTD / totalUnitsMTD : 0
    },
    totalPerUnit: {
      daily: dailyTotalUnits > 0 ? (dailyTotalGross + dailyFI.fiTotal) / dailyTotalUnits : 0,
      monthly: monthlyTotalUnits > 0 ? (monthlyTotalGross + monthlyFI.fiTotal) / monthlyTotalUnits : 0,
      dailyTarget: totalUnitsDaily > 0 ? (totalGrossDaily + fiGrossDaily) / totalUnitsDaily : 0,
      monthlyTarget: totalUnitsMTD > 0 ? (totalGrossMTD + fiGrossMTD) / totalUnitsMTD : 0
    },
    // No target: an over-allowance isn't a goal, it's a leak to keep small.
    // Null means no deals carried a trade in that window.
    avgOverUnder: {
      daily: avgOverUnder(dailyActivities),
      monthly: avgOverUnder(monthlyActivities),
      dailyDeals: tradeCount(dailyActivities),
      monthlyDeals: tradeCount(monthlyActivities)
    }
  };
}

export default function SalesScoreboard({ user, managedOwner } = {}) {
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState('');
  const [selectedDealershipObj, setSelectedDealershipObj] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [rawLogs, setRawLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [hasTargets, setHasTargets] = useState(true);
  // Month to date alone by default. Activity is logged at the end of the day and
  // deals are often entered at the end of the week, so the daily column reads
  // blank or short for most of the day it covers. "Show Daily" brings it back.
  const [showDaily, setShowDaily] = useState(false);

  useEffect(() => {
    async function fetchDealerships() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // In owner / managed-owner view, scope to that owner's dealerships so a
        // site manager viewing a specific owner doesn't see (or pick) every
        // dealership on the platform. Otherwise use the caller's own access.
        const isOwnerView = user?.role === 'OWNER' || managedOwner;
        let url = isOwnerView ? `${API_URL}/api/owners/dealerships` : `${API_URL}/api/dealerships`;
        if (managedOwner && isOwnerView) {
          url += `?ownerId=${managedOwner.id}`;
        }
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error('Failed to load dealerships');
        const json = await res.json();
        setDealerships(json.dealerships || []);
        if (json.dealerships && json.dealerships.length > 0) {
          setSelectedDealership(json.dealerships[0].id);
          setSelectedDealershipObj(json.dealerships[0]);
        }
      } catch (err) {
        setError(err.message || 'Failed to load dealerships');
      } finally {
        setLoading(false);
      }
    }
    fetchDealerships();
  }, [user, managedOwner]);

  useEffect(() => {
    if (!selectedDealership) return;
    async function fetchMetrics() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Fetch data in parallel
        const [logsRes, salesRes, targetsRes, leaderboardRes] = await Promise.all([
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/daily-logs`, { headers }),
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/sales-activities`, { headers }),
          fetch(`${API_URL}/api/targets/${selectedDealership}`, { headers }),
          fetch(`${API_URL}/api/analytics/leaderboard/sales?dealershipIds=${selectedDealership}&period=monthly`, { headers })
        ]);

        const logsData = logsRes.ok ? await logsRes.json() : { entries: [] };
        const salesData = salesRes.ok ? await salesRes.json() : { activities: [] };
        const targetsData = targetsRes.ok ? await targetsRes.json() : { frontEnd: {} };

        // Debug leaderboard response
        if (!leaderboardRes.ok) {
          console.warn('Leaderboard request failed:', leaderboardRes.status, await leaderboardRes.text());
        }
        const leaderboardData = leaderboardRes.ok ? await leaderboardRes.json() : { leaderboard: [] };

        // When the dealership disables "always show F&I", unclosed deals don't
        // count toward any report figures until they're marked closed/committed.
        const visibleActivities = (selectedDealershipObj?.showUnclosedFI !== false)
          ? (salesData.activities || [])
          : (salesData.activities || []).filter((a) => a.closed);

        const computedMetrics = computeMetrics(
          logsData.entries || [],
          visibleActivities,
          targetsData.frontEnd || {},
          selectedDealershipObj,
          selectedDate
        );

        setMetrics(computedMetrics);
        setHasTargets(Array.isArray(targetsData.frontEnd?.monthlyTargets) && targetsData.frontEnd.monthlyTargets.length > 0);
        setLeaderboard(leaderboardData.leaderboard || []);
        setRawLogs(logsData.entries || []);
      } catch (err) {
        setError(err.message || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    }
    fetchMetrics();
  }, [selectedDealership, selectedDealershipObj, selectedDate]);

  const handleDealershipChange = (dealershipId) => {
    setSelectedDealership(dealershipId);
    const dealershipObj = dealerships.find(d => d.id === dealershipId);
    setSelectedDealershipObj(dealershipObj || null);
  };

  const fitSpecialistStats = useMemo(() => {
    const refDate = new Date(selectedDate);
    const month = refDate.getMonth();
    const year = refDate.getFullYear();

    const monthlyLogs = rawLogs.filter(e => {
      const d = new Date(e.date);
      return d.getUTCFullYear() === year && d.getUTCMonth() === month;
    });

    const bySpecialist = {};
    monthlyLogs.forEach(entry => {
      const isNamedSpecialist = !entry.userId && entry.specialistValue;
      const roles = Array.isArray(entry.user?.roles) ? entry.user.roles : [];
      const isUserFitSpecialist = roles.includes('Fit Specialist');
      if (!isUserFitSpecialist && !isNamedSpecialist) return;

      const id = entry.userId || entry.specialistValue || entry.user?.id;
      const name = entry.user?.name || entry.specialistName || 'Unknown';
      if (!bySpecialist[id]) {
        bySpecialist[id] = { id, name, greets: 0, sitDowns: 0, testRides: 0, writeUps: 0 };
      }
      bySpecialist[id].greets += Number(entry.greets) || 0;
      bySpecialist[id].sitDowns += Number(entry.sitDowns) || 0;
      bySpecialist[id].testRides += Number(entry.testRides) || 0;
      bySpecialist[id].writeUps += Number(entry.writeUps) || 0;
    });

    return Object.values(bySpecialist).sort((a, b) => b.greets - a.greets);
  }, [rawLogs, selectedDate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.mozFullScreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleFullscreen = () => {
    const scoreboardElement = document.querySelector('.sales-scoreboard');
    if (!fullscreen && scoreboardElement) {
      if (scoreboardElement.requestFullscreen) {
        scoreboardElement.requestFullscreen();
      } else if (scoreboardElement.webkitRequestFullscreen) {
        scoreboardElement.webkitRequestFullscreen();
      } else if (scoreboardElement.mozRequestFullScreen) {
        scoreboardElement.mozRequestFullScreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
    }
  };

  const percentClass = (pct) => {
    if (pct >= 80) return 'high';
    if (pct >= 50) return 'mid';
    return 'low';
  };

  const getTrendIndicator = (pct) => {
    if (pct >= 80) return { label: 'On Track', class: 'trend-on-track' };
    if (pct >= 50) return { label: 'Caution', class: 'trend-caution' };
    return { label: 'Behind', class: 'trend-behind' };
  };

  if (loading) return <div className="scoreboard-loading">Loading...</div>;
  if (error) return <div className="scoreboard-error">{error}</div>;
  if (!dealerships.length) return <div className="scoreboard-error">No dealerships found.</div>;
  if (!metrics) return <div className="scoreboard-loading">Loading metrics...</div>;
  if (!hasTargets) return <NoTargetsPlaceholder />;

  const m = metrics;

  // Over/under has no target, so it skips the meter and percent that
  // renderMetricCard assumes. Positive = money given away on trades.
  const renderOverUnderCard = (metric) => {
    const block = (subLabel, value, deals) => {
      const tone = value === null || value === 0 ? 'mid' : value > 0 ? 'low' : 'high';
      const label = value === null
        ? '—'
        : `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`;
      return (
        <div className="kpi-period-block">
          <div className="kpi-sub">{subLabel}</div>
          <div className="kpi-value">{label}</div>
          <div className={`kpi-percent ${tone}`}>
            {value === null ? 'No trades' : value > 0 ? 'Over' : value < 0 ? 'Under' : 'At book'}
          </div>
          <div className="kpi-target">
            {deals === 0 ? '—' : `${deals} deal${deals === 1 ? '' : 's'} with a trade`}
          </div>
        </div>
      );
    };
    return (
      <div className="kpi-card">
        <div className="kpi-label">Avg Over/Under per Bike</div>
        <div className="kpi-compare-multi">
          {showDaily && block('Daily', metric.daily, metric.dailyDeals || 0)}
          {block('MTD', metric.monthly, metric.monthlyDeals || 0)}
        </div>
      </div>
    );
  };

  // Helper to render a metric card. MTD always shows; Daily joins it when the
  // header's "Show Daily" is on.
  const renderMetricCard = (label, metric, formatter = (v) => v) => {
    const periodBlock = (subLabel, value, target) => {
      const percent = calculatePercent(value, target);
      const trend = getTrendIndicator(percent);
      return (
        <div className="kpi-period-block">
          <div className="kpi-sub">{subLabel}</div>
          <div className="kpi-value">{formatter(value)}</div>
          <div className="kpi-target">Target: {formatter(target)}</div>
          <div className={`kpi-percent ${percentClass(percent)}`}>{percent}%</div>
          <div className="kpi-meter-bar">
            <div className={`kpi-meter-fill ${percentClass(percent)}`} style={{ width: `${Math.min(percent, 100)}%` }}></div>
          </div>
          <div className={`trend-indicator ${trend.class}`}>{trend.label}</div>
        </div>
      );
    };

    return (
      <div className="kpi-card">
        <div className="kpi-label">{label}</div>
        <div className="kpi-compare-multi">
          {showDaily && periodBlock('Daily', metric.daily, metric.dailyTarget)}
          {periodBlock('MTD', metric.monthly, metric.monthlyTarget)}
        </div>
      </div>
    );
  };

  return (
    <div className="sales-scoreboard">
      <div className="scoreboard-header">
        <h1><span style={{ color: '#ff8c00', fontSize: '1.2em' }}>{dealerships.find(d => d.id === selectedDealership)?.name || ''}: Sales Scoreboard</span></h1>
        <div className="scoreboard-controls">
          <ScoreboardDropdown
            options={dealerships.map(d => ({ value: d.id, label: d.name }))}
            value={selectedDealership}
            onChange={handleDealershipChange}
          />
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            label=""
          />
          <button
            className={`period-toggle-btn ${showDaily ? 'active' : ''}`}
            onClick={() => setShowDaily((prev) => !prev)}
          >
            {showDaily ? 'Hide Daily' : 'Show Daily'}
          </button>
          <button className="fullscreen-btn" onClick={handleFullscreen}>
            {fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>
      </div>
      <div className="scoreboard-main-content">
        <div className="scoreboard-metrics-container">
          {/* Group 1: Activity Metrics */}
          <div className="metric-group">
            <div className="kpi-grid">
              {renderMetricCard('Greets', m.greets)}
              {renderMetricCard('Sit Downs', m.sitDowns)}
              {renderMetricCard('Test Rides', m.testRides)}
              {renderMetricCard('Write Ups', m.writeUps)}
            </div>
          </div>

          {/* Group 2: Sales Metrics */}
          <div className="metric-group">
            <div className="kpi-grid">
              {renderMetricCard('New Unit Sales', m.newUnits)}
              {renderMetricCard('New Unit Gross Profit', m.newGross, formatCurrency)}
              {renderMetricCard('Used Unit Sales', m.usedUnits)}
              {renderMetricCard('Used Unit Gross Profit', m.usedGross, formatCurrency)}
            </div>
          </div>

          {/* Group 3: Frontend Metrics */}
          <div className="metric-group">
            <div className="kpi-grid">
              {renderMetricCard('Frontend Total', m.frontendTotal, formatCurrency)}
              {renderMetricCard('Frontend per Unit', m.frontendPerUnit, formatCurrency)}
            </div>
          </div>

          {/* Group 4: F&I Metrics */}
          <div className="metric-group">
            <div className="kpi-grid">
              {renderMetricCard('F&I Reserve Gross Profit', m.fiReserve, formatCurrency)}
              {renderMetricCard('F&I Product Gross Profit', m.fiProduct, formatCurrency)}
              {renderMetricCard('F&I Total', m.fiTotal, formatCurrency)}
              {renderOverUnderCard(m.avgOverUnder)}
              {renderMetricCard('F&I per Unit', m.fiPerUnit, formatCurrency)}
              {renderMetricCard('Total per Unit', m.totalPerUnit, formatCurrency)}
            </div>
          </div>
        </div>
        <div className="scoreboard-sidebar">
          <h3 className="sidebar-title">Top Performers</h3>
          <div className="sidebar-content">
            {leaderboard.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', textAlign: 'center', margin: '20px 0' }}>
                No data available
              </p>
            ) : (
              leaderboard.slice(0, 10).map((performer, idx) => (
                <div key={performer.employeeId || idx} className="leaderboard-item">
                  <div className="leaderboard-rank">#{idx + 1}</div>
                  <div className="leaderboard-details">
                    <div className="leaderboard-name">{performer.name || 'Unknown'}</div>
                    <div className="leaderboard-stats">
                      <span>{performer.sales || performer.bikesSold || 0} units</span>
                      <span className="stat-separator">•</span>
                      <span>{formatCurrency(performer.totalGross || 0)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {fitSpecialistStats.length > 0 && (
            <>
              <h3 className="sidebar-title" style={{ marginTop: '24px' }}>Fit Specialists (MTD)</h3>
              <div className="sidebar-content">
                {fitSpecialistStats.map((spec, idx) => (
                  <div key={spec.id || idx} className="leaderboard-item">
                    <div className="leaderboard-rank">#{idx + 1}</div>
                    <div className="leaderboard-details">
                      <div className="leaderboard-name">{spec.name}</div>
                      <div className="leaderboard-stats">
                        <span>{spec.greets} Gr</span>
                        <span className="stat-separator">•</span>
                        <span>{spec.sitDowns} SD</span>
                        <span className="stat-separator">•</span>
                        <span>{spec.testRides} TR</span>
                        <span className="stat-separator">•</span>
                        <span>{spec.writeUps} WU</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
